import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Wrench, ShieldCheck, Eye, Hand } from 'lucide-react';
import DriverStatusBadge from './DriverStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPATIBILITY_META = {
  compatible:     { label: 'Compatible',     cls: 'bg-emerald-50 text-emerald-700' },
  limited:        { label: 'Limited',        cls: 'bg-amber-50 text-amber-700' },
  not_compatible: { label: 'Not compatible', cls: 'bg-rose-50 text-rose-700' },
  unknown:        { label: 'Unknown',        cls: 'bg-slate-100 text-slate-600' },
};
const PRIORITY_META = {
  now:           { label: 'Now',           cls: 'bg-rose-50 text-rose-700' },
  later:         { label: 'Later',         cls: 'bg-amber-50 text-amber-700' },
  not_relevant:  { label: 'Not relevant',  cls: 'bg-slate-100 text-slate-600' },
};

const COMPONENT_TYPES = [
  '', 'p1_meter', 'smart_plug', 'inverter', 'home_battery', 'ev_charger',
  'heat_pump', 'boiler', 'airco', 'smart_thermostat', 'fuse_box_3_phase',
  'insulation', 'ventilation', 'glass', 'other',
];

export default function ComponentsTab({ token }) {
  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    segment: 'residential', component_type: '', compatible_status: '',
    requires_installer: '', priority: '', source: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { segment: filters.segment || 'residential' };
      const r = await axios.get(`${API}/admin/components`, {
        headers: { Authorization: `Bearer ${token}` }, params,
      });
      setItems(r.data.components || []);
      // also load drivers (admin view, full list — for linking + control checks)
      const dr = await axios.get(`${API}/admin/drivers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrivers(dr.data.drivers || []);
    } catch {
      toast.error('Kon componenten niet laden');
    } finally { setLoading(false); }
  }, [token, filters.segment]);

  useEffect(() => { load(); }, [load]);

  const driverById = useMemo(() => {
    const m = new Map();
    drivers.forEach(d => m.set(d.driver_id, d));
    return m;
  }, [drivers]);

  const filtered = useMemo(() => items.filter(c => {
    if (filters.component_type && c.component_type !== filters.component_type) return false;
    if (filters.compatible_status && c.compatible_status !== filters.compatible_status) return false;
    if (filters.requires_installer === 'yes' && !c.requires_installer) return false;
    if (filters.requires_installer === 'no' && c.requires_installer) return false;
    if (filters.priority && c.priority !== filters.priority) return false;
    if (filters.source && c.source !== filters.source) return false;
    return true;
  }), [items, filters]);

  const patch = async (component_id, body) => {
    try {
      await axios.patch(`${API}/admin/components/${component_id}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Bijgewerkt');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update mislukt');
    }
  };

  const counts = useMemo(() => ({
    total: items.length,
    installer: items.filter(c => c.requires_installer).length,
    no_driver: items.filter(c => !c.driver_id).length,
    can_control: items.filter(c => c.can_control).length,
  }), [items]);

  return (
    <div className="space-y-4" data-testid="components-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-lg font-medium flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Components</h2>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-muted" data-testid="components-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <div className="border rounded p-3">Totaal<div className="text-2xl font-light">{counts.total}</div></div>
        <div className="border rounded p-3 bg-amber-50">Installateur nodig<div className="text-2xl font-light text-amber-800">{counts.installer}</div></div>
        <div className="border rounded p-3 bg-zinc-50">Handmatige route<div className="text-2xl font-light text-zinc-800">{counts.no_driver}</div></div>
        <div className="border rounded p-3 bg-emerald-50">Can control<div className="text-2xl font-light text-emerald-800">{counts.can_control}</div></div>
      </div>

      <div className="border rounded p-3 grid grid-cols-2 md:grid-cols-6 gap-2 text-sm" data-testid="components-filters">
        <select value={filters.segment} onChange={e => setFilters(f => ({ ...f, segment: e.target.value }))} className="border rounded px-2 py-1">
          <option value="residential">residential</option>
          <option value="mkb">mkb</option>
          <option value="vve">vve</option>
        </select>
        <select value={filters.component_type} onChange={e => setFilters(f => ({ ...f, component_type: e.target.value }))} className="border rounded px-2 py-1">
          {COMPONENT_TYPES.map(c => <option key={c} value={c}>{c || 'Alle types'}</option>)}
        </select>
        <select value={filters.compatible_status} onChange={e => setFilters(f => ({ ...f, compatible_status: e.target.value }))} className="border rounded px-2 py-1">
          <option value="">Alle compat</option>
          {Object.keys(COMPATIBILITY_META).map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <select value={filters.requires_installer} onChange={e => setFilters(f => ({ ...f, requires_installer: e.target.value }))} className="border rounded px-2 py-1">
          <option value="">Installateur?</option>
          <option value="yes">Installateur</option>
          <option value="no">Geen installateur</option>
        </select>
        <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))} className="border rounded px-2 py-1">
          <option value="">Alle prio's</option>
          {Object.keys(PRIORITY_META).map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <select value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))} className="border rounded px-2 py-1">
          <option value="">Alle bronnen</option>
          <option value="intake">intake</option>
          <option value="manual">manual</option>
          <option value="admin">admin</option>
          <option value="agent_suggestion">agent_suggestion</option>
        </select>
      </div>

      <div className="border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="p-2">Label / Brand</th>
              <th className="p-2">Type</th>
              <th className="p-2">Compat</th>
              <th className="p-2">Driver</th>
              <th className="p-2 text-center">Meet</th>
              <th className="p-2 text-center">Stuur</th>
              <th className="p-2 text-center">Install</th>
              <th className="p-2">Prio</th>
              <th className="p-2">Bron</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Geen componenten in dit segment.</td></tr>}
            {filtered.map(c => {
              const drv = c.driver_id ? driverById.get(c.driver_id) : null;
              const driverAdviceOnly = drv && ['none', 'advice_only', 'installer_locked'].includes(drv.control_level);
              return (
                <tr key={c.component_id} className="border-t hover:bg-muted/50" data-testid={`component-row-${c.component_id}`}>
                  <td className="p-2">
                    <div className="font-medium">{c.label || '—'}</div>
                    <div className="text-xs text-muted-foreground">{c.brand} {c.model}</div>
                  </td>
                  <td className="p-2 text-xs">{c.component_type}</td>
                  <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-xs ${COMPATIBILITY_META[c.compatible_status]?.cls || ''}`}>{c.compatible_status || 'unknown'}</span></td>
                  <td className="p-2">
                    {drv ? <DriverStatusBadge driver={drv} /> : <span className="inline-flex items-center gap-1 text-xs text-zinc-600"><Hand className="w-3 h-3" /> Handmatige route</span>}
                  </td>
                  <td className="p-2 text-center">{c.can_measure ? '✓' : '·'}</td>
                  <td className="p-2 text-center">
                    {c.can_control
                      ? <span title={driverAdviceOnly ? 'Driver is advice_only; can_control wordt verstoord' : ''} className={driverAdviceOnly ? 'text-rose-600' : 'text-emerald-700'}>✓</span>
                      : <span className="text-muted-foreground">·</span>}
                  </td>
                  <td className="p-2 text-center">{c.requires_installer ? <Wrench className="w-3.5 h-3.5 inline text-amber-700" /> : '·'}</td>
                  <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-xs ${PRIORITY_META[c.priority]?.cls || ''}`}>{c.priority || '—'}</span></td>
                  <td className="p-2 text-xs">{c.source}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Server enforced: een component met driver `advice_only`/`read_only` accepteert nooit
        `can_control=true`. Componenten zonder driver tonen "Handmatige route beschikbaar".
      </p>
    </div>
  );
}
